import { AfterContentInit, Component, forwardRef, Input } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ClassNames } from '../../../constants/class-names.constants';
import { IRadioOption, RadioOption } from '../../../models/common';
import { BaseFormControlComponent } from '../base-form-control.component';

/**
 * @group Forms
 * @component Radio List
 *
 * @export
 * @class LsqRadioListComponent
 * @extends {BaseFormControlComponent<boolean>}
 * @implements {AfterContentInit}
 * @property {string} name The name of the radio input.
 * @property {any[]} options An array of options you want to add to the list of radio buttons.
 * @property {boolean} [vertical] Display radio buttons vertically and default to `true`.
 * @property {string} [parentClass] Adds to the `class` on the radio button input.
 * @property {string} [labelClass] Adds to the `class` on the label.
 */

@Component({
    selector: 'lsq-radio-list',
    template: `
        <ng-container *ngIf="options.length > 0">
            <ul [ngClass]="getClassList()">
                <li
                    *ngFor="let option of options"
                    [style.margin-bottom]="vertical ? '0.7rem' : '0'"
                >
                    <lsq-radio
                        [id]="option.id"
                        [label]="option.label"
                        [labelClassName]="labelClassName"
                        [value]="option.value"
                        [name]="name"
                        [ngModel]="model"
                        (ngModelChange)="handleChange($event)"
                        [disabled]="disabled"
                    >
                    </lsq-radio>
                </li>
            </ul>
        </ng-container>
    `,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => LsqRadioListComponent),
            multi: true
        }
    ]
})
export class LsqRadioListComponent extends BaseFormControlComponent<any>
    implements AfterContentInit {
    @Input() public name: string;
    @Input() public options: IRadioOption[] = [];
    @Input() public vertical = true;
    @Input() public parentClass?: string;
    @Input() public labelClassName?: string;

    public ngAfterContentInit(): void {
        this.buildOptions();
    }

    public getClassList(): string {
        const listClass = this.vertical ? ClassNames.ListUnstyled : ClassNames.ListInline;
        return this.parentClass ? `${listClass} ${this.parentClass}` : `${listClass}`;
    }

    public buildOptions(): void {
        if (this.options.length > 0) {
            this.options = [...this.options].map((option, index) => {
                const radioOption = new RadioOption();

                if (option && typeof option !== 'object') {
                    radioOption.label = `${option}`;
                    radioOption.value = option;
                    radioOption.id = `${this.name}-${typeof option}-${index}`;
                } else {
                    return option;
                }

                radioOption.checked = this.model && this.model === option;
                return radioOption;
            });
        }
    }
}
